import { pickT } from "@repo/shared";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import type { PortfolioDisplayConfig } from "@/lib/types/flow";
import { PortfolioDisplayWidget } from "../portfolio-display-widget";
import { T } from "./portfolio-display.i18n";
import type { PortfolioInfo } from "./types";

type PortfolioDisplayProps = {
  portfolioId: string | null;
  config: PortfolioDisplayConfig;
  language: string;
  chartColors?: string[] | null;
  portfolioInfo: PortfolioInfo;
  /**
   * Phase 2 S1 — engine reported `kind: "snapshot"` for this component on this portfolio.
   * Wizard threads `componentHasError` (gated on `showValidationErrors` per H1-H4 pattern).
   * When set AND `portfolio.data` is null, replace the misleading "no portfolio for score X"
   * fallback with a clear snapshot-missing placeholder pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
};

export function PortfolioDisplay({
  portfolioId,
  config,
  language,
  chartColors,
  portfolioInfo,
  hasValidationError = false,
}: PortfolioDisplayProps) {
  const t = pickT(T, language);
  const portfolio = usePortfolioSnapshot("portfolio", portfolioId);

  if (portfolio.isLoading) return null;

  if (!portfolio.data) {
    if (portfolioInfo.isCustomPortfolio) {
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.noProductsYet}
        </div>
      );
    }
    if (!portfolioInfo.hasRiskAssessment) {
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.completeRiskAssessment}
        </div>
      );
    }
    if (!portfolioInfo.hasInvestmentAmount) {
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.enterInvestmentAmount}
        </div>
      );
    }
    // Phase 2 S1 — engine D20 N/A guard means the 3 earlier prerequisite branches
    // (no products / no risk / no amount) wouldn't trigger `hasValidationError`. When we
    // reach this fallback AND hasValidationError is true, prerequisites are met but the
    // portfolio snapshot row is missing (system-bug case). The misleading
    // "no portfolio for score X" message is replaced with a clear snapshot-missing
    // placeholder pointing the user at the regenerate banner above.
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noPortfolioForScore(String(portfolioInfo.riskScore ?? ""))}
      </div>
    );
  }

  const snapshot =
    typeof portfolio.data === "string"
      ? (() => {
          try {
            return JSON.parse(portfolio.data as string);
          } catch {
            return null;
          }
        })()
      : portfolio.data;

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoadingPortfolio}</div>;
  }

  const products = snapshot.products as Array<{ amount?: number }> | undefined;
  const totalAmount = products?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

  if (totalAmount === 0) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {portfolioInfo.isCustomPortfolio ? t.portfolioNoAmounts : t.enterInvestmentAmount}
      </div>
    );
  }

  return (
    <PortfolioDisplayWidget
      snapshot={snapshot}
      showChart={config.showChart === true}
      showTable={config.showTable !== false}
      chartColors={chartColors}
      showPortfolioName={config.showPortfolioName !== false}
      style={config.style}
      title={config.title}
      description={config.description}
      language={language}
      portfolioId={portfolioId}
    />
  );
}
